import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import BrokersSection from './components/BrokersSection';
import FeatureSection from './components/FeatureSection';
import BenefitsSection from './components/BenefitsSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import Dashboard from './pages/Dashboard';
import TradingEdge  from './components/TradingEdge';
import PricingPage from './pages/PricingPage';
import AnalysisPage from './pages/AnalysisPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Disclaimers from './pages/Disclaimers';
import CookiePolicy from './pages/CookiePolicy';
import TrialPolicy from './pages/TrialPolicy';
import CookieConsent from './components/CookieConsent';
import CookieButton from './components/CookieButton';
import { CookieProvider } from './components/CookieContext';
import NewsletterPopup from './components/NewsletterPopup';
// Import page Company
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import PressPage from './pages/PressPage';
import InvestorsPage from './pages/InvestorsPage';
// Import Documentation Page
import DocumentationPage from './pages/DocumentationPage';
// Import Blog Pages
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
// Import 404 Page
import NotFoundPage from './pages/NotFoundPage';
// Import AI-Powered Trading Landing Page
import AIPoweredTradingLandingPage from './pages/AIPoweredTradingLandingPage';
// Import page Resources
import './assets/styles/App.css';
// Import hero section override CSS - must be last to take precedence
import './assets/styles/HeroSectionOverride.css';

// External redirect component
const ExternalRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  
  return <div>Redirecting to {to}...</div>;
};

// ScrollToTop component to reset scroll position on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    // Scroll to top of page on route change, but not for hash links
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  
  return null;
};

const App: React.FC = () => {
  // SEO metadata
  const pageTitle = "Price on Rails | AI-Powered Trading Autocomplete | Translate Stocks";
  const pageDescription = "Experience Price on Rails - the revolutionary AI autocomplete for intraday charts. Make faster, more informed trading decisions with our predictive analytics platform.";
  const canonicalUrl = "https://translatestocks.com/";

  // Reset session storage for newsletter popup during development
  // Only uncomment this in development
  useEffect(() => {
    // For development - clear the session storage flag to test popup
    sessionStorage.removeItem('newsletter_popup_shown');
    // Also clear the newsletter cookie for testing
    // document.cookie = "newsletter_subscribed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("App mounted, reset newsletter popup session storage");
  }, []);

  // Structured data for rich snippets
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Price on Rails",
    "applicationCategory": "FinanceApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "operatingSystem": "Web-based",
    "description": "AI-powered trading platform with predictive analytics for stocks, futures, and crypto markets."
  };

  return (
    <CookieProvider>
      <div className="app">
        <ScrollToTop />
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <link rel="canonical" href={canonicalUrl} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:image" content="https://translatestocks.com/images/price-on-rails-og.jpg" />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={canonicalUrl} />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content="https://translatestocks.com/images/price-on-rails-twitter.jpg" />
          
          {/* Additional SEO tags */}
          <meta name="keywords" content="trading platform, AI trading, predictive analytics, stock market, day trading, automated trading, Price on Rails, Translate Stocks" />
          
          {/* Structured data */}
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>

        <Header />
        
        <main>
          <Routes>
            <Route path="/" element={
              <>
                <HeroSection />
                <BrokersSection />
                <FeatureSection 
                  title="What is Translate Stocks?"
                  description={`
                    Translate Stocks is a comprehensive trading ecosystem that combines a powerful cloud-based platform with web resources and mobile capabilities.

                    The cloud platform includes three key applications: TS Studio Chart for market analysis and building trading robots, Price on Rails (PR) as an AI trading assistant that provides personalized guidance, and Order Router (RTS) which connects to broker accounts for seamless order execution.

                    Along with daily analysis for over 500 companies, free trading signals, market news, and company fundamentals, Translate Stocks offers a web remote control that extends functionality to any device, allowing users to trade on the go, monitor profits and losses, and generate financial reports from anywhere.
                  `}
                  imageSrc="/images/ts-platform.jpg"
                  imageAlt="Cloud-based trading platform"
                  reverse={true}
                />
                <FeatureSection 
                  title="Price on Rails: Our Crowning Innovation"
                  description="At the heart of the platform is Price on Rails—a groundbreaking 'autocomplete' feature for intraday charts. By projecting the next potential price moves in real time, Price on Rails empowers traders to make faster, more informed decisions."
                  imageSrc="/images/price-on-rails-chart.jpg"
                  imageAlt="Price on Rails chart prediction interface"
                  reverse={false}                  
                />
                <FeatureSection 
                  title="Introducing the Agentic Bot"
                  description="We've taken Price on Rails a step further with the Agentic Bot: an automated trading companion that works seamlessly within Translate Stocks. Whether you use the full desktop interface or opt for our convenient web view, the Agentic Bot can trade alongside you."
                  imageSrc="/images/agentic-bot-interface.png"
                  imageAlt="Agentic Bot trading interface"
                  reverse={true}               
                  showConsultation={true}
                />
                <BenefitsSection />
                <CTASection 
                  title="The Opportunity Is Now"
                  description="Translate Stocks merges leading-edge technology, robust market insights, and a frictionless user experience under one distinctive brand."
                  buttonText="Start Trading Smarter"
                  buttonLink="/pricing"
                />
              </>
            } />
            
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/pricing" element={<PricingPage />} />  
            <Route path="/pricing-monthly" element={<ExternalRedirect to="https://pay.translatestocks.com/b/9AQ6roa6dgKa5jO00H"   />} />
            <Route path="/pricing-yearly" element={<ExternalRedirect to="https://pay.translatestocks.com/b/6oE9DA9291Pg7rWbJq" />} />
            <Route path="/pricing-lifetime" element={<ExternalRedirect to="https://pay.translatestocks.com/b/14k030bahgKa9A48xh" />} />
            <Route path="/pricing-trial" element={<ExternalRedirect to="https://pay.translatestocks.com/b/6oEcPM0vD65wcMg3cW" />} />
            <Route path="/price-on-rails" element={<TradingEdge />} />
            <Route path="/demo" element={<Navigate to="/price-on-rails#video-demo" replace />} />
            <Route path="/features" element={<AnalysisPage />} />
            <Route path="/analysis" element={<AnalysisPage />} />
            <Route path="/stock-analysis" element={<AnalysisPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/disclaimers" element={<Disclaimers />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/login" element={<ExternalRedirect to="https://www.translatestocks.com/login" />} />
            <Route path="/setup" element={<ExternalRedirect to="https://www.translatestocks.com/setup" />} />
            <Route path="/news" element={<ExternalRedirect to="https://www.translatestocks.com/news" />} />
            <Route path="/research" element={<ExternalRedirect to="https://ai.translatestocks.com/research" />} />
            <Route path="/trial-policy" element={<TrialPolicy />} />

            <Route path="/about" element={<AboutPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/investors" element={<InvestorsPage />} />
            <Route path="/docs" element={<DocumentationPage />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            
            {/* Blog routes */}
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:postId" element={<BlogPostPage />} />
            
            {/* Unlisted AI Trading Landing Page */}
            <Route path="/ai-trading" element={<AIPoweredTradingLandingPage />} />
            
            {/* 404 - Not Found - Catch all unmatched routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        
        <Footer />
        <CookieConsent />
        <CookieButton />
        <NewsletterPopup delay={3000} showOncePerSession={true} />
      </div>
    </CookieProvider>
  );
};

export default App;